import { Badge } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import {
  ServerErrorsContext,
  ServerErrorsProvider,
} from 'contexts/admin/server-errors.context';
import { format } from 'date-fns-tz';
import parseISO from 'date-fns/parseISO';
import { TABLES } from 'enums/tables';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { StringHelper } from 'lib_ts/classes/string.helper';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IServerErrorSummary } from 'lib_ts/interfaces/i-server-error';
import { useContext, useState } from 'react';
import useMiscStore from 'stores/use-misc-store';
import { ServerErrorDetailsDialog } from './details';
import { ServerErrorsTableToolbar } from './toolbar';

const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

const COLUMNS: ITypedTableColumn<IServerErrorSummary>[] = [
  {
    label: 'common.created',
    key: '_created',
    formatFn: (m) =>
      format(parseISO(m._created), LOCAL_DATETIME_FORMAT, {
        timeZone: LOCAL_TIMEZONE,
      }),
    sortRowsFn: (a, b, dir) =>
      StringHelper.safeSort(a._created, b._created) * dir,
  },
  {
    label: 'common.category',
    key: 'category',
    formatFn: (m) => StringHelper.capitalize(m.level),
  },
  {
    label: 'common.level',
    key: 'level',
    formatFn: (m) => (
      <Badge color={NotifyHelper.getColorFromLevel(m.level)}>
        {m.level.toUpperCase()}
      </Badge>
    ),
  },
  {
    label: 'common.summary',
    key: 'summary',
    disableSort: true,
  },
];

export const ServerErrorsTableHoC = () => {
  return (
    <ServerErrorsProvider>
      <ServerErrorsTable />
    </ServerErrorsProvider>
  );
};

export const ServerErrorsTable = () => {
  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const { filtered } = useContext(ServerErrorsContext);

  const [dialogID, setDialogID] = useState<string>();

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminServerErrors,
    total: filtered.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: '_created',
      dir: -1,
    },
  };

  const select: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: (error: IServerErrorSummary | undefined) =>
      setDialogID(error?._id),
  };

  return (
    <ErrorBoundary identifier="TeamsTable">
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.SECTION}
        table={
          <CommonTableHoC
            dataTestID="ServerErrors"
            identifier="ServerErrorsTable"
            toolbarContent={<ServerErrorsTableToolbar />}
            displayColumns={COLUMNS}
            displayData={filtered}
            enableListener={!anyOpenDialogs}
            {...pagination}
            {...select}
            {...sort}
            vFlex
          />
        }
      />

      {dialogID && (
        <ServerErrorDetailsDialog
          id={dialogID}
          identifier="ViewServerErrorDialog"
          onClose={() => setDialogID(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
